import { useEffect, useRef, useState } from 'react';
// Components/ui
import { Box, Card, CardContent, Chip, CircularProgress, IconButton, styled, TextField, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
  CustomPropertyDatePicker,
  CustomPropertyDateRangePicker,
  CustomPropertyInput,
  CustomPropertyOptionSelect,
  CustomPropertyTimePicker,
  CustomPropertyTimeRangePicker,
  CustomPropertyListSingle,
  CustomPropertyListObject,
  FieldInfoLabel,
} from './components';
// Types
import type { IContactField } from '@trii/types/dist/Contacts/contacts';
import { ContactField_type, Property } from '@trii/types/dist/Contacts';
import type { UserTrii } from '@trii/types/dist/Users';
// Services
import { updateEntity } from '../../services/api';
// Hooks (edit state)
import type UseEditContact from '../../hooks/useEditContact/types/UseEditContact';
import type UseEditBusiness from '../../hooks/useEditBusiness/types/UseEditBusiness';
// Utils
import dayjs, { Dayjs } from 'dayjs';

const StyledIconButton = styled(IconButton)(() => ({
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  '& .MuiSvgIcon-root': {
    width: '0.8em',
    height: '0.8em',
  },
  marginLeft: '15px',
}));

const StyledSaveCancelButton = styled(IconButton)(() => ({
  '& .MuiSvgIcon-root': {
    width: '0.8em',
    height: '0.8em',
  },
}));

const ContactBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  borderRadius: theme.shape.borderRadius,
  minWidth: '45%',
  '&:hover .edit-icon': {
    opacity: 1,
  },
}));

const FieldName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  alignSelf: 'center',
  color: theme.palette.text.primary,
  letterSpacing: -0.5,
  padding: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
}));

const FieldValue = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: '0.875rem',
  padding: '2.5px 14px 8.5px 0px',
  color: theme.palette.text.primary,
  wordWrap: 'break-word',
  maxHeight: '80px',
  overflow: 'auto',
  whiteSpace: 'break-spaces',
}));

type Props = {
  property: Property;
  entity: UseEditContact | UseEditBusiness;
  entityType: 'contact' | 'business';
  entityId: string;
  customContactFields: IContactField[];
  userTrii?: UserTrii | null;
  baseUrl?: string;
  spaceId?: string;
  onEntityUpdated?: (entity: unknown) => void;
};

const EntityCustomFieldLabel = ({
  property,
  entity,
  entityType,
  entityId,
  customContactFields,
  userTrii,
  baseUrl,
  spaceId,
  onEntityUpdated,
}: Props) => {
  const { customProperties } = entity.field;
  const { setCustomProperties } = entity.action;

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const titleRef = useRef<HTMLElement | null>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  const userDateFormat = (userTrii?.regCon_dateFormat || 'YYYY/MM/DD').toUpperCase();
  const userTimeFormat = userTrii?.regCon_timeFormat || '24';

  // Determina si el tipo de campo es numérico
  const isNumericType = (type: ContactField_type) =>
    type === ContactField_type.NUMBER_INTEGER ||
    type === ContactField_type.NUMBER_DECIMAL ||
    type === ContactField_type.NUMBER_CURRENCY ||
    type === ContactField_type.NUMBER_ACCOUNTING ||
    type === ContactField_type.CURRENCY;

  const shouldBeNumeric = (nameKey: string, type: ContactField_type) => {
    const lower = nameKey.toLowerCase();
    if (lower === 'dni') return true; // fuerza DNI como numérico
    return isNumericType(type);
  };

  // Normaliza el valor según el tipo del campo
  const normalizeValue = (nameKey: string, type: ContactField_type, value: any) => {
    if (value === '' || value === null || value === undefined) return null;

    if (shouldBeNumeric(nameKey, type)) {
      const num = Number(value);
      return Number.isNaN(num) ? value : num;
    }

    return value;
  };

  // Parsea valores de tipo lista que pueden llegar como JSON string o como array
  const parseListValue = (val: any): any[] => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const toggleEdit = () => setIsEditing((prev) => !prev);

  const handleCancel = () => {
    setIsEditing(false);
    entity.action.resetCustomProperty(property.nameKey);
  };

  const handleSave = async () => {
    if (!baseUrl || !spaceId) return;
    if (!entityId) return;

    setIsSaving(true);

    const current = customProperties.find((p) => p.nameKey === property.nameKey) || property;

    const { nameKey, value } = current;

    // Definición del campo (si existe)
    const fieldDef = customContactFields.find((f) => f.nameKey === nameKey);

    // Tipo efectivo: primero el de la definición, si no existe usamos el del property
    const effectiveType: ContactField_type = fieldDef?.type ?? current.type;

    // Preparar el valor según el tipo de campo
    let finalValue: any;

    // Manejar campos de tipo TIME
    if (effectiveType === ContactField_type.TIME) {
      const dayjsValue = dayjs(value);
      if (dayjsValue.isValid()) {
        // Enviar solo el string de hora formateado
        finalValue = dayjsValue.format(userTimeFormat === '24' ? 'HH:mm' : 'hh:mm');
      } else {
        finalValue = value;
      }
    }
    // Manejar campos de tipo DATE
    else if (effectiveType === ContactField_type.DATE) {
      const dayjsValue = dayjs(value);
      if (dayjsValue.isValid()) {
        // Enviar el ISO string para fechas
        finalValue = dayjsValue.toISOString();
      } else {
        finalValue = value;
      }
    }
    // Para otros tipos, usar normalizeValue
    else if (
      effectiveType === ContactField_type.LIST_SINGLE ||
      effectiveType === ContactField_type.LIST_OBJECT
    ) {
      // El backend (MongoDB ObjectSerializer) no acepta JArray en Property.Value;
      // se serializa como JSON string para evitar el error de deserialización BSON.
      finalValue = Array.isArray(value) ? JSON.stringify(value) : value;
    }
    else {
      finalValue = normalizeValue(nameKey, effectiveType, value);
    }

    const updatedContactData = {
      properties: [
        {
          nameKey,
          type: effectiveType,
          value: finalValue,
        },
      ],
    };

    try {
      const updated = await updateEntity({
        baseUrl,
        spaceId,
        entityType,
        entityId,
        body: updatedContactData as any,
      });
      const nextProperties = customProperties.map((p: Property) =>
        p.nameKey === nameKey
          ? {
              ...p,
              type: effectiveType,
              value: finalValue,
            }
          : p,
      );
      setCustomProperties(nextProperties);
      onEntityUpdated?.(updated);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  const getPropertyName = (propertyNameKey: string) => {
    const foundField = customContactFields.find((field) => field.nameKey === propertyNameKey);

    return foundField?.name || propertyNameKey;
  };

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const check = () => {
      const next = el.scrollWidth > el.clientWidth;
      setIsTitleTruncated((prev) => (prev === next ? prev : next));
    };

    check();

    if (typeof ResizeObserver !== 'undefined') {
      let rafId = 0;
      const ro = new ResizeObserver(() => {
        if (typeof window === 'undefined') {
          check();
          return;
        }
        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = window.requestAnimationFrame(() => {
          check();
        });
      });
      ro.observe(el);
      return () => {
        if (typeof window !== 'undefined' && rafId) window.cancelAnimationFrame(rafId);
        ro.disconnect();
      };
    }

    if (typeof window === 'undefined') return;
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('resize', check);
    };
  }, [customContactFields, property.nameKey]);

  const handleInputChange = (name: string, value: any) => {
    const updatedValue =
      property.type === ContactField_type.DATERANGE
        ? Array.isArray(value) && value[0] && value[1]
          ? `${dayjs(value[0]).toISOString()} - ${dayjs(value[1]).toISOString()}`
          : ''
        : property.type === ContactField_type.DATE
          ? dayjs(value).isValid()
            ? dayjs(value).toISOString()
            : 'Invalid Date'
          : property.type === ContactField_type.TIME
            ? dayjs(value).isValid()
              ? dayjs(value).toISOString()
              : 'Invalid Time'
            : value;

    const updatedProperties = customProperties.map((property) =>
      property.nameKey === name ? { ...property, value: updatedValue } : property,
    );

    setCustomProperties(updatedProperties);
  };

  const { type, nameKey, value } = property;

  // Verifica si el valor existe y no está vacío
  const hasValue = (val: any) => {
    if (val === null || val === undefined) return false;

    // Si es string, verifica que no esté vacío
    if (typeof val === 'string') {
      return val.trim().length > 0;
    }

    return true;
  };

  const isFieldRequired =
    customContactFields.find((field) => field.nameKey === property.nameKey)?.requiredField ?? false;

  const showRequiredIndicator = isFieldRequired && !hasValue(value);

  const NUMERIC_TYPES = [
    ContactField_type.NUMBER_INTEGER,
    ContactField_type.NUMBER_DECIMAL,
    ContactField_type.NUMBER_CURRENCY,
    ContactField_type.NUMBER_ACCOUNTING,
    ContactField_type.CURRENCY,
  ];

  const isCurrentValueOutOfRange = () => {
    const fieldDef = customContactFields.find((f) => f.nameKey === nameKey);
    if (!fieldDef) return false;
    const { min, max } = fieldDef;
    if (min == null && max == null) return false;
    if (!NUMERIC_TYPES.includes(type)) return false;
    if (value === '' || value == null) return false;
    const num = Number(value);
    if (Number.isNaN(num)) return false;
    return (min != null && num < min) || (max != null && num > max);
  };

  const EditField = () => {
    if (type === ContactField_type.DATE)
      return (
        <CustomPropertyDatePicker
          value={value ? dayjs(value) : null}
          handleChange={(newValue) => handleInputChange(nameKey, newValue?.toDate())}
          dateFormat={userDateFormat}
        />
      );
    else if (type === ContactField_type.TIME)
      return (
        <CustomPropertyTimePicker
          value={
            // Usar valueDate si está disponible, sino intentar parsear value
            (property as any)?.valueDate
              ? dayjs((property as any).valueDate)
              : value
                ? dayjs(value)
                : null
          }
          handleChange={(newValue) => handleInputChange(nameKey, newValue)}
          userTimeFormat={userTimeFormat}
        />
      );
    else if (type === ContactField_type.DATERANGE)
      return (
        <CustomPropertyDateRangePicker
          value={
            value && typeof value === 'string' && value.includes(' - ')
              ? ([dayjs(value.split(' - ')[0]), dayjs(value.split(' - ')[1])] as [Dayjs, Dayjs])
              : null
          }
          handleChange={(newValue) =>
            handleInputChange(nameKey, [newValue[0]?.toDate?.(), newValue[1]?.toDate?.()])
          }
          userDateFormat={userDateFormat}
        />
      );
    else if (type === ContactField_type.TIMERANGE)
      return (
        <CustomPropertyTimeRangePicker
          value={
            Array.isArray(value) && value.length === 2
              ? ([dayjs(value[0]), dayjs(value[1])] as [Dayjs, Dayjs])
              : null
          }
          handleChange={(newValue) =>
            handleInputChange(nameKey, [newValue[0]?.toDate?.(), newValue[1]?.toDate?.()])
          }
          userTimeFormat={userTimeFormat}
        />
      );
    else if (type === ContactField_type.OPTIONS)
      return (
        <CustomPropertyOptionSelect
          value={typeof value === 'string' ? value : ''}
          handleChange={(newValue) => handleInputChange(nameKey, newValue)}
          options={
            customContactFields.find((field) => field.nameKey === nameKey)?.typeOptions ?? []
          }
        />
      );
    else if (type === ContactField_type.LIST_SINGLE)
      return (
        <CustomPropertyListSingle
          nameKey={nameKey}
          name={getPropertyName(nameKey)}
          value={parseListValue(value)}
          onChange={handleInputChange}
          description={customContactFields.find((f) => f.nameKey === nameKey)?.description}
        />
      );
    else if (type === ContactField_type.LIST_OBJECT)
      return (
        <CustomPropertyListObject
          nameKey={nameKey}
          name={getPropertyName(nameKey)}
          value={parseListValue(value)}
          schema={customContactFields.find((f) => f.nameKey === nameKey)?.listItemSchema ?? []}
          onChange={handleInputChange}
          description={customContactFields.find((f) => f.nameKey === nameKey)?.description}
        />
      );
    else
      return (
        <CustomPropertyInput
          type={type}
          nameKey={nameKey}
          value={value}
          onChange={handleInputChange}
          min={customContactFields.find((f) => f.nameKey === nameKey)?.min}
          max={customContactFields.find((f) => f.nameKey === nameKey)?.max}
        />
      );
  };

  return (
    <ContactBox
      className={`fadein ${
        property.type === ContactField_type.LONGTEXT ||
        property.type === ContactField_type.LIST_OBJECT
          ? 'col-12'
          : 'col-5'
      }`}
    >
      <Box display="flex" position="relative" minHeight="25px" alignItems="center">
        {isTitleTruncated ? (
          <Tooltip title={getPropertyName(property.nameKey)} arrow>
            <FieldName ref={titleRef} flexGrow={isEditing ? 1 : 0} noWrap>
              <FieldInfoLabel
                label={getPropertyName(property.nameKey)}
                description={customContactFields.find((f) => f.nameKey === property.nameKey)?.description}
              />
              {showRequiredIndicator && (
                <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                  *
                </Box>
              )}
            </FieldName>
          </Tooltip>
        ) : (
          <FieldName ref={titleRef} flexGrow={isEditing ? 1 : 0} noWrap>
            <FieldInfoLabel
              label={getPropertyName(property.nameKey)}
                description={customContactFields.find((f) => f.nameKey === property.nameKey)?.description}
            />
            {showRequiredIndicator && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Box>
            )}
          </FieldName>
        )}
        {isEditing ? (
          <>
            <StyledSaveCancelButton
              size="small"
              onClick={handleSave}
              disabled={isSaving || isCurrentValueOutOfRange()}
            >
              {isSaving ? <CircularProgress size={24} /> : <SaveIcon />}
            </StyledSaveCancelButton>
            <StyledSaveCancelButton size="small" onClick={handleCancel}>
              <CancelIcon />
            </StyledSaveCancelButton>
          </>
        ) : (
          <StyledIconButton className="edit-icon" size="small" onClick={toggleEdit}>
            <EditIcon />
          </StyledIconButton>
        )}
      </Box>

      <Box
        width={
          isEditing ||
          property.type === ContactField_type.LONGTEXT ||
          property.type === ContactField_type.LIST_OBJECT
            ? '100%'
            : '50%'
        }
        position="relative"
      >
        {isEditing ? (
          isSaving ? null : (
            EditField()
          )
        ) : property.type === ContactField_type.LIST_SINGLE ? (
          (() => {
            const items = parseListValue(property.value);
            return items.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: '2.5px' }}>
                {items.map((item: string, i: number) => (
                  <Chip key={i} label={item} size="small" />
                ))}
              </Box>
            ) : (
              <FieldValue>-</FieldValue>
            );
          })()
        ) : property.type === ContactField_type.LIST_OBJECT ? (
          (() => {
            const items = parseListValue(property.value);
            const schema =
              customContactFields.find((f) => f.nameKey === nameKey)?.listItemSchema ?? [];
            return items.length > 0 ? (
              <Box sx={{ pt: '2.5px', pb: '8.5px' }}>
                {(items as Record<string, any>[]).map((item, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      {schema.map((field) => (
                        <TextField
                          key={field.key}
                          size="small"
                          variant="standard"
                          label={field.label}
                          value={item[field.key] ?? ''}
                          InputProps={{ readOnly: true, disableUnderline: true }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          sx={{ mb: 0.5, '& .MuiInputBase-input': { cursor: 'default' } }}
                        />
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <FieldValue>-</FieldValue>
            );
          })()
        ) : (
          <FieldValue>
            {hasValue(property?.value)
              ? property.type === ContactField_type.DATERANGE
                ? (() => {
                    if (typeof property.value === 'string') {
                      const parts = property.value.split(' - ');
                      if (parts.length === 2) {
                        const [start, end] = parts;
                        const startParsed = dayjs(start);
                        const endParsed = dayjs(end);
                        return `${startParsed.isValid() ? startParsed.format(userDateFormat) : '-'} - ${
                          endParsed.isValid() ? endParsed.format(userDateFormat) : '-'
                        }`;
                      }
                    } else if (Array.isArray(property.value) && property.value.length === 2) {
                      return `${dayjs(property.value[0]).format(
                        userDateFormat,
                      )} - ${dayjs(property.value[1]).format(userDateFormat)}`;
                    }
                    return `${property.value}`;
                  })()
                : property.type === ContactField_type.TIMERANGE
                  ? (() => {
                      if (Array.isArray(property.value) && property.value.length === 2) {
                        return `${dayjs(property.value[0]).format(
                          userTimeFormat === '24' ? 'HH:mm' : 'hh:mm',
                        )} - ${dayjs(property.value[1]).format(
                          userTimeFormat === '24' ? 'HH:mm' : 'hh:mm',
                        )}`;
                      }
                      return `${property.value}`;
                    })()
                  : property.type === ContactField_type.TIME
                    ? (() => {
                        try {
                          const timeValue = (property as any).valueDate || property.value;
                          return `${dayjs(timeValue).format(userTimeFormat === '24' ? 'HH:mm' : 'hh:mm A')}`;
                        } catch (error) {
                          return `${property.value}`;
                        }
                      })()
                    : property.type === ContactField_type.DATE
                      ? (() => {
                          try {
                            return `${dayjs(property.value).format(userDateFormat)}`;
                          } catch (error) {
                            return `${property.value}`;
                          }
                        })()
                      : `${property?.value}`
              : null}
          </FieldValue>
        )}
      </Box>
    </ContactBox>
  );
};

export default EntityCustomFieldLabel;
